import { Button, Device, Facility, LANE_NUMBER_MAX, LANE_NUMBER_MIN, LaneLine, TurnType, isValidLaneNumber } from "@app/shared";
import { useState } from "react";
import { useFacilityCoordsHelper } from "../../../../helpers/coordsHelpers";
import { calculateCardinalDirection, detectLaneLineStreetName, generateUniqueLaneName } from "../../../../helpers";
import { EnumSelect, Input, InputLabel, InputRow, NumericInput, Select, SelectOption } from "../../inputs";
import { ColorPicker } from "../ColorPicker";

type LaneLineConfigProps = {
    laneLine: LaneLine;
    onChange: (zone: LaneLine) => void;
    isEditable?: boolean;
    isCollapsed: boolean;
    availableDevices: Device[];
    facility: Facility;
};

export const LaneLineConfig: React.FC<LaneLineConfigProps> = ({ laneLine, onChange, isCollapsed, isEditable = false, availableDevices, facility }) => {
    const coordsHelper = useFacilityCoordsHelper(facility);
    const [isDetecting, setIsDetecting] = useState(false);

    const handleOnChange = (key: keyof LaneLine, value: LaneLine[keyof LaneLine]) => {
        onChange(new LaneLine({ ...laneLine, [key]: value }));
    };
    const handleChangeLaneNumber = (laneNumber: number | undefined) => {
        if (laneNumber === undefined || laneNumber == null) {
            onChange(new LaneLine({ ...laneLine, laneNumber: laneNumber }));
            return;
        }
        // The device uses this number as a bit position in its Lane mask, so anything outside
        // LANE_NUMBER_MIN..MAX cannot be represented and would silently attribute every target on
        // this line to unrelated lanes. NumericInput clamps to the same range; this guards paste
        // and programmatic changes that bypass the clamp.
        if (!isValidLaneNumber(laneNumber)) {
            return;
        }
        onChange(new LaneLine({ ...laneLine, laneNumber: laneNumber }));
    };

    const handleDetectStreet = async () => {
        if (!laneLine.points || laneLine.points.length < 2) {
            alert("Lane line must have at least 2 points");
            return;
        }

        setIsDetecting(true);
        try {
            const direction = calculateCardinalDirection(laneLine.points[0], laneLine.points[1], coordsHelper);
            const detectedInfo = await detectLaneLineStreetName(laneLine, direction, coordsHelper);

            if (!detectedInfo) {
                console.warn("Could not detect street names for this lane line");
                return;
            }

            const existingLaneLines = facility.laneLines || [];
            const finalName = generateUniqueLaneName(detectedInfo.formattedName, existingLaneLines, laneLine.id);

            onChange(new LaneLine({ ...laneLine, name: finalName }));
        } catch (error) {
            console.error("Error detecting streets:", error);
        } finally {
            setIsDetecting(false);
        }
    };

    const deviceOptions: SelectOption[] = availableDevices.map((d) => ({ label: d.name, value: d.id || "" }));

    if (isCollapsed) {
        return null;
    }
    return (
        <>
            <InputRow>
                <InputLabel>Color</InputLabel>
                <div className="flex place-items-center">
                    <ColorPicker value={laneLine.color ?? ""} onChange={(newColor) => handleOnChange("color", newColor)} disabled={!isEditable} />
                    <div className="flex-grow ml-5">
                        <Input value={laneLine.color ?? ""} onChange={(e) => handleOnChange("color", e.target.value)} disabled={!isEditable} />
                    </div>
                </div>
            </InputRow>
            <InputRow>
                <Input onChange={(e) => handleOnChange("name", e.target.value)} label="Lane Line Name" value={laneLine.name} disabled={!isEditable} />
            </InputRow>
            <InputRow>
                <EnumSelect enumType={TurnType} label="Turn Type" emptyOptionText="Select Turn Type" onChange={(e) => handleOnChange("turnType", e)} value={laneLine.turnType || TurnType.Straight} disabled={!isEditable} />
            </InputRow>
            <InputRow>
                <Select options={deviceOptions} label="Device" emptyOptionText="Select Device" value={laneLine.deviceId || ""} onChange={(e) => handleOnChange("deviceId", e.target.value)} disabled={!isEditable} />
            </InputRow>
            <InputRow>
                <NumericInput
                    label={`Lane Number (${LANE_NUMBER_MIN}-${LANE_NUMBER_MAX})`}
                    onChange={(e) => handleChangeLaneNumber(e)}
                    value={laneLine.laneNumber}
                    min={LANE_NUMBER_MIN}
                    max={LANE_NUMBER_MAX}
                    allowFloat={false}
                    debounce={0}
                    disabled={!isEditable}
                />
            </InputRow>
            <InputRow>
                <Button
                    onClick={handleDetectStreet}
                    disabled={!isEditable || isDetecting}
                    className="w-full"
                    title="Auto-detect and populate lane name based on street names and turn type"
                >
                    <i className="fa fa-location-arrow mr-2"></i>
                    {isDetecting ? "Detecting Streets..." : "Auto-Detect Lane Name"}
                </Button>
            </InputRow>
        </>
    );
};
